import type {
  ActualVsBudgetCompanyFixture,
  ActualVsBudgetDataAvailability,
  ActualVsBudgetExperimentViewModel,
  ActualVsBudgetMetricFixture,
  ActualVsBudgetMetricKey,
  ActualVsBudgetMonthFixture,
  ActualVsBudgetPlRowFixture,
  ActualVsBudgetPlRowKey,
} from '@/features/actual-vs-budget/types'
import {
  roundFixtureValue,
  sumCompleteFixtureValues,
} from '@/features/actual-vs-budget/fixture-financials'

const juneFiscalYearMonthEnds = [
  '2025-07-31',
  '2025-08-31',
  '2025-09-30',
  '2025-10-31',
  '2025-11-30',
  '2025-12-31',
  '2026-01-31',
  '2026-02-28',
  '2026-03-31',
  '2026-04-30',
  '2026-05-31',
  '2026-06-30',
] as const

const decemberFiscalYearMonthEnds = [
  '2026-01-31',
  '2026-02-28',
  '2026-03-31',
  '2026-04-30',
  '2026-05-31',
  '2026-06-30',
  '2026-07-31',
  '2026-08-31',
  '2026-09-30',
  '2026-10-31',
  '2026-11-30',
  '2026-12-31',
] as const

interface MetricSeed {
  key: ActualVsBudgetMetricKey
  label: string
  monthlyActuals: readonly number[]
  monthlyBudgets: readonly number[]
  annualBudget?: number
  monthlyForecasts: readonly number[]
  annualForecast?: number
  priorYearActuals: readonly number[]
}

interface CompanySeed {
  id: string
  name: string
  fund: string
  fiscalYearLabel: string
  fiscalYearEnd: { month: number; day: number }
  periodEnds: readonly string[]
  closedMonthCount: number
  budgetAvailability: ActualVsBudgetDataAvailability
  forecastAvailability: ActualVsBudgetDataAvailability
  metrics: readonly MetricSeed[]
}

function getAnnualBudget(seed: MetricSeed) {
  if (seed.annualBudget === undefined) {
    throw new Error(`Annual Budget is required for ${seed.key}`)
  }

  return seed.annualBudget
}

function getAnnualForecast(seed: MetricSeed) {
  if (seed.annualForecast === undefined) {
    throw new Error(`Annual Forecast is required for ${seed.key}`)
  }

  return seed.annualForecast
}

function buildMetricFixture(
  seed: MetricSeed,
  company: Pick<
    CompanySeed,
    | 'closedMonthCount'
    | 'budgetAvailability'
    | 'forecastAvailability'
    | 'periodEnds'
  >
): ActualVsBudgetMetricFixture {
  const series = [
    seed.monthlyActuals,
    seed.monthlyBudgets,
    seed.monthlyForecasts,
    seed.priorYearActuals,
  ]

  if (
    company.closedMonthCount < 1 ||
    company.closedMonthCount > company.periodEnds.length ||
    series.some(
      (values) =>
        values.length !== company.periodEnds.length ||
        values.some((value) => !Number.isFinite(value))
    )
  ) {
    throw new Error(`Invalid monthly fixture contract for ${seed.key}`)
  }

  const budgetIsAvailable = company.budgetAvailability === 'phased'
  const forecastIsAvailable = company.forecastAvailability === 'phased'

  return {
    key: seed.key,
    label: seed.label,
    direction: 'higher-is-better',
    unit: 'usd-millions',
    fullYearBudget:
      company.budgetAvailability === 'unavailable'
        ? null
        : company.budgetAvailability === 'annual-only'
          ? getAnnualBudget(seed)
          : seed.monthlyBudgets.reduce((total, value) => total + value, 0),
    fullYearForecast:
      company.forecastAvailability === 'unavailable'
        ? null
        : company.forecastAvailability === 'annual-only'
          ? getAnnualForecast(seed)
          : seed.monthlyForecasts.reduce((total, value) => total + value, 0),
    months: company.periodEnds.map((periodEnd, index) => ({
      fiscalMonth: index + 1,
      periodEnd,
      actual:
        index < company.closedMonthCount ? seed.monthlyActuals[index] : null,
      budget: budgetIsAvailable ? seed.monthlyBudgets[index] : null,
      forecast: forecastIsAvailable ? seed.monthlyForecasts[index] : null,
      priorYearActual: seed.priorYearActuals[index],
    })),
  }
}

type PlSeriesKey = keyof Pick<
  ActualVsBudgetMonthFixture,
  'actual' | 'budget' | 'forecast' | 'priorYearActual'
>

const costRatioBySeries: Record<PlSeriesKey, number> = {
  actual: 0.59,
  budget: 0.57,
  forecast: 0.58,
  priorYearActual: 0.6,
}

function derivePlValue(
  key: ActualVsBudgetPlRowKey,
  revenue: number | null,
  adjustedEbitda: number | null,
  series: PlSeriesKey
) {
  if (revenue === null || adjustedEbitda === null) return null

  const cogs = roundFixtureValue(revenue * costRatioBySeries[series], 1)
  const grossProfit = roundFixtureValue(revenue - cogs, 1)

  switch (key) {
    case 'revenue':
      return revenue
    case 'cogs':
      return cogs
    case 'gross-profit':
      return grossProfit
    case 'gross-margin':
      return revenue === 0 ? null : roundFixtureValue(grossProfit / revenue, 4)
    case 'operating-expenses':
      return roundFixtureValue(grossProfit - adjustedEbitda, 1)
    case 'adjusted-ebitda':
      return adjustedEbitda
    case 'net-income':
      return roundFixtureValue(adjustedEbitda * 0.62, 1)
  }
}

const plRowDefinitions: readonly Omit<
  ActualVsBudgetPlRowFixture,
  'fullYearBudget' | 'fullYearForecast' | 'months' | 'priorCompletedYearActual'
>[] = [
  {
    key: 'revenue',
    label: 'Revenue',
    direction: 'higher-is-better',
    unit: 'usd-millions',
    indent: 0,
    emphasis: 'standard',
  },
  {
    key: 'cogs',
    label: 'Cost of Services / COGS',
    direction: 'lower-is-better',
    unit: 'usd-millions',
    indent: 1,
    emphasis: 'standard',
  },
  {
    key: 'gross-profit',
    label: 'Gross Profit',
    direction: 'higher-is-better',
    unit: 'usd-millions',
    indent: 0,
    emphasis: 'subtotal',
  },
  {
    key: 'gross-margin',
    label: 'Gross Margin',
    direction: 'higher-is-better',
    unit: 'percentage-points',
    indent: 1,
    emphasis: 'standard',
  },
  {
    key: 'operating-expenses',
    label: 'Operating Expenses',
    direction: 'lower-is-better',
    unit: 'usd-millions',
    indent: 1,
    emphasis: 'standard',
  },
  {
    key: 'adjusted-ebitda',
    label: 'Adjusted EBITDA',
    direction: 'higher-is-better',
    unit: 'usd-millions',
    indent: 0,
    emphasis: 'subtotal',
  },
  {
    key: 'net-income',
    label: 'Net Income',
    direction: 'higher-is-better',
    unit: 'usd-millions',
    indent: 0,
    emphasis: 'total',
  },
]

function buildPlRows(
  metrics: readonly ActualVsBudgetMetricFixture[]
): readonly ActualVsBudgetPlRowFixture[] {
  const revenue = metrics.find(({ key }) => key === 'revenue')
  const adjustedEbitda = metrics.find(({ key }) => key === 'adjusted-ebitda')

  if (!revenue || !adjustedEbitda) {
    throw new Error('Revenue and Adjusted EBITDA fixtures are required for P&L')
  }

  const priorYearRevenue = sumCompleteFixtureValues(
    revenue.months.map(({ priorYearActual }) => priorYearActual)
  )
  const priorYearAdjustedEbitda = sumCompleteFixtureValues(
    adjustedEbitda.months.map(({ priorYearActual }) => priorYearActual)
  )

  return plRowDefinitions.map((definition) => ({
    ...definition,
    fullYearBudget: derivePlValue(
      definition.key,
      revenue.fullYearBudget,
      adjustedEbitda.fullYearBudget,
      'budget'
    ),
    fullYearForecast: derivePlValue(
      definition.key,
      revenue.fullYearForecast,
      adjustedEbitda.fullYearForecast,
      'forecast'
    ),
    priorCompletedYearActual: derivePlValue(
      definition.key,
      priorYearRevenue,
      priorYearAdjustedEbitda,
      'priorYearActual'
    ),
    months: revenue.months.map((revenueMonth, index) => {
      const adjustedEbitdaMonth = adjustedEbitda.months[index]

      if (!adjustedEbitdaMonth) {
        throw new Error(`Missing Adjusted EBITDA month ${index + 1}`)
      }

      return {
        fiscalMonth: revenueMonth.fiscalMonth,
        periodEnd: revenueMonth.periodEnd,
        actual: derivePlValue(
          definition.key,
          revenueMonth.actual,
          adjustedEbitdaMonth.actual,
          'actual'
        ),
        budget: derivePlValue(
          definition.key,
          revenueMonth.budget,
          adjustedEbitdaMonth.budget,
          'budget'
        ),
        forecast: derivePlValue(
          definition.key,
          revenueMonth.forecast,
          adjustedEbitdaMonth.forecast,
          'forecast'
        ),
        priorYearActual: derivePlValue(
          definition.key,
          revenueMonth.priorYearActual,
          adjustedEbitdaMonth.priorYearActual,
          'priorYearActual'
        ),
      }
    }),
  }))
}

function buildCompanyFixture(seed: CompanySeed): ActualVsBudgetCompanyFixture {
  const metrics = seed.metrics.map((metric) => buildMetricFixture(metric, seed))

  return {
    id: seed.id,
    name: seed.name,
    fund: seed.fund,
    currency: 'USD',
    fiscalYearLabel: seed.fiscalYearLabel,
    fiscalYearEnd: seed.fiscalYearEnd,
    latestClosedPeriodEnd: seed.periodEnds[seed.closedMonthCount - 1],
    budgetAvailability: seed.budgetAvailability,
    forecastAvailability: seed.forecastAvailability,
    metrics,
    plRows: buildPlRows(metrics),
  }
}

const companySeeds: readonly CompanySeed[] = [
  {
    id: 'alder-ridge',
    name: 'Alder Ridge Components',
    fund: 'Greybull Fund III',
    fiscalYearLabel: 'FY2026',
    fiscalYearEnd: { month: 6, day: 30 },
    periodEnds: juneFiscalYearMonthEnds,
    closedMonthCount: 9,
    budgetAvailability: 'phased',
    forecastAvailability: 'phased',
    metrics: [
      {
        key: 'revenue',
        label: 'Revenue',
        monthlyActuals: [8.2, 8.5, 8.1, 8.8, 9.1, 9.3, 9.5, 9.4, 9.7, 0, 0, 0],
        monthlyBudgets: [8, 8.2, 8.3, 8.5, 8.7, 8.9, 9, 9.2, 9.4, 9.6, 9.8, 10],
        monthlyForecasts: [
          8.2, 8.5, 8.1, 8.8, 9.1, 9.3, 9.5, 9.4, 9.7, 9.8, 10, 10.2,
        ],
        priorYearActuals: [
          7.6, 7.9, 7.8, 8.1, 8.3, 8.5, 8.7, 8.8, 9, 9.1, 9.3, 9.5,
        ],
      },
      {
        key: 'adjusted-ebitda',
        label: 'Adjusted EBITDA',
        monthlyActuals: [1.3, 1.4, 1.2, 1.5, 1.6, 1.7, 1.8, 1.7, 1.9, 0, 0, 0],
        monthlyBudgets: [
          1.2, 1.3, 1.3, 1.4, 1.5, 1.5, 1.6, 1.6, 1.7, 1.8, 1.8, 1.9,
        ],
        monthlyForecasts: [
          1.3, 1.4, 1.2, 1.5, 1.6, 1.7, 1.8, 1.7, 1.9, 1.9, 2, 2,
        ],
        priorYearActuals: [
          1, 1.1, 1.1, 1.2, 1.3, 1.3, 1.4, 1.5, 1.5, 1.6, 1.6, 1.7,
        ],
      },
    ],
  },
  {
    id: 'blue-haven',
    name: 'Blue Haven Field Services',
    fund: 'Greybull Fund II',
    fiscalYearLabel: 'FY2026',
    fiscalYearEnd: { month: 12, day: 31 },
    periodEnds: decemberFiscalYearMonthEnds,
    closedMonthCount: 3,
    budgetAvailability: 'phased',
    forecastAvailability: 'phased',
    metrics: [
      {
        key: 'revenue',
        label: 'Revenue',
        monthlyActuals: [5.4, 5.1, 4.9, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        monthlyBudgets: [
          5.8, 5.9, 6, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 7,
        ],
        monthlyForecasts: [
          5.4, 5.1, 4.9, 5.7, 5.8, 5.9, 6, 6.1, 6.2, 6.3, 6.4, 6.5,
        ],
        priorYearActuals: [
          5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 5.8, 5.9, 6, 6.1, 6.2, 6.3,
        ],
      },
      {
        key: 'adjusted-ebitda',
        label: 'Adjusted EBITDA',
        monthlyActuals: [0.7, 0.6, 0.5, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        monthlyBudgets: [0.9, 0.9, 1, 1, 1, 1.1, 1.1, 1.1, 1.2, 1.2, 1.2, 1.3],
        monthlyForecasts: [
          0.7, 0.6, 0.5, 0.8, 0.8, 0.9, 0.9, 0.9, 1, 1, 1, 1.1,
        ],
        priorYearActuals: [
          0.8, 0.8, 0.8, 0.9, 0.9, 0.9, 1, 1, 1, 1.1, 1.1, 1.1,
        ],
      },
    ],
  },
  {
    id: 'cinder-peak',
    name: 'Cinder Peak Packaging',
    fund: 'Greybull Fund III',
    fiscalYearLabel: 'FY2026',
    fiscalYearEnd: { month: 12, day: 31 },
    periodEnds: decemberFiscalYearMonthEnds,
    closedMonthCount: 3,
    budgetAvailability: 'unavailable',
    forecastAvailability: 'annual-only',
    metrics: [
      {
        key: 'revenue',
        label: 'Revenue',
        monthlyActuals: [6.1, 6.3, 6.4, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        monthlyBudgets: Array(12).fill(0),
        monthlyForecasts: Array(12).fill(0),
        annualForecast: 80.4,
        priorYearActuals: [
          5.8, 5.9, 6, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 6.8, 6.9,
        ],
      },
      {
        key: 'adjusted-ebitda',
        label: 'Adjusted EBITDA',
        monthlyActuals: [1, 1.1, 1.1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        monthlyBudgets: Array(12).fill(0),
        monthlyForecasts: Array(12).fill(0),
        annualForecast: 15.1,
        priorYearActuals: [
          0.8, 0.9, 0.9, 1, 1, 1.1, 1.1, 1.2, 1.2, 1.3, 1.3, 1.4,
        ],
      },
    ],
  },
  {
    id: 'dovetail',
    name: 'Dovetail Specialty Health',
    fund: 'Greybull Fund II',
    fiscalYearLabel: 'FY2026',
    fiscalYearEnd: { month: 6, day: 30 },
    periodEnds: juneFiscalYearMonthEnds,
    closedMonthCount: 9,
    budgetAvailability: 'annual-only',
    forecastAvailability: 'unavailable',
    metrics: [
      {
        key: 'revenue',
        label: 'Revenue',
        monthlyActuals: [4.4, 4.5, 4.7, 4.8, 4.9, 5, 5.1, 5.2, 5.3, 0, 0, 0],
        monthlyBudgets: Array(12).fill(0),
        annualBudget: 58.2,
        monthlyForecasts: Array(12).fill(0),
        priorYearActuals: [
          4, 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 4.8, 4.9, 5, 5.1,
        ],
      },
      {
        key: 'adjusted-ebitda',
        label: 'Adjusted EBITDA',
        monthlyActuals: [0.7, 0.7, 0.8, 0.8, 0.9, 0.9, 1, 1, 1.1, 0, 0, 0],
        monthlyBudgets: Array(12).fill(0),
        annualBudget: 10.3,
        monthlyForecasts: Array(12).fill(0),
        priorYearActuals: [
          0.6, 0.6, 0.7, 0.7, 0.7, 0.8, 0.8, 0.8, 0.9, 0.9, 0.9, 1,
        ],
      },
    ],
  },
]

export const actualVsBudgetCompanyFixtures =
  companySeeds.map(buildCompanyFixture)

export const actualVsBudgetExperimentViewModel: ActualVsBudgetExperimentViewModel =
  {
    badgeLabel: 'Experimental mock',
    disclosure:
      'Illustrative concept using sample data. Not connected to Chronograph and not intended for investment decisions.',
    companies: actualVsBudgetCompanyFixtures,
  }
